import type { PixelGlyph } from '@cards/cards';
import type { ReactElement } from 'react';
import { memo } from 'react';
import { COLORS } from './kit/tokens.js';

export interface AuthoredGlyphProps {
  glyph?: PixelGlyph | undefined;
  testId: string;
  visualId: string;
  size?: number;
  className?: string;
}

interface GlyphPath {
  readonly fill: string;
  readonly d: string;
}

/**
 * Authored glyphs are static catalog objects that keep their identity for the
 * life of the page (see the memo note below), so the conversion is paid once per
 * distinct glyph — lazily, because a deck's worth of cards is never all on
 * screen. Weak so a sandbox-only or test-only glyph literal can still be
 * collected.
 */
const glyphPathCache = new WeakMap<PixelGlyph, readonly GlyphPath[]>();

/**
 * Collapses a pixel grid into one `<path>` per palette colour.
 *
 * A 20×20 authored glyph is up to four hundred filled cells, and one `<rect>`
 * each is what made a glyph expensive to mount and to re-rasterise. Runs of the
 * same colour along a row become a single subpath, and every run of one colour
 * shares one element, so the node count drops to the size of the palette (three
 * or five) no matter how detailed the art is.
 *
 * The result is pixel-identical, and not by careful tuning: each cell holds
 * exactly one colour, so nothing overlaps and paint order cannot matter, and the
 * subpaths are axis-aligned on integer coordinates just like the rects they
 * replace — `shape-rendering: crispEdges` rasterises them the same way.
 */
function buildGlyphPaths(glyph: PixelGlyph): readonly GlyphPath[] {
  const byFill = new Map<string, string[]>();
  for (const [y, row] of glyph.rows.entries()) {
    let x = 0;
    while (x < row.length) {
      const pixel = row[x]!;
      if (pixel === '.') {
        x += 1;
        continue;
      }
      let end = x + 1;
      while (end < row.length && row[end] === pixel) end += 1;
      // An out-of-palette digit paints nothing, exactly as the per-pixel
      // renderer skipped a `fill === undefined` cell.
      const fill = glyph.colors[Number(pixel) - 1];
      if (fill !== undefined) {
        const width = end - x;
        const subpaths = byFill.get(fill);
        const subpath = `M${x} ${y}h${width}v1h-${width}z`;
        if (subpaths === undefined) byFill.set(fill, [subpath]);
        else subpaths.push(subpath);
      }
      x = end;
    }
  }
  return [...byFill].map(([fill, subpaths]) => ({ fill, d: subpaths.join('') }));
}

function glyphPaths(glyph: PixelGlyph): readonly GlyphPath[] {
  const cached = glyphPathCache.get(glyph);
  if (cached !== undefined) return cached;
  const built = buildGlyphPaths(glyph);
  glyphPathCache.set(glyph, built);
  return built;
}

/**
 * Shared renderer for authored code-native pixel symbols.
 *
 * Memoised, and not as a micro-optimisation: a hand holds a dozen glyphs and the
 * board a dozen more, so an unmemoised glyph made every unrelated re-render (a
 * hand reorder mid-drag, the sandbox sheet changing height) reconcile the whole
 * set. All props are primitives except the glyph itself, which comes straight
 * from the static presentation catalog and keeps its identity for the life of the
 * page — which is also what makes the path cache above safe.
 */
export const AuthoredGlyph = memo(function AuthoredGlyph({
  glyph,
  testId,
  visualId,
  size = 40,
  className = 'cards-card-glyph',
}: AuthoredGlyphProps): ReactElement {
  const width = glyph?.rows[0]?.length ?? 9;
  const height = glyph?.rows.length ?? 9;
  // Pixel art scales by whole pixels only (01 §10): the drawn size is the
  // largest multiple of the grid that fits the asked size, never less than 1×.
  // `data-grid` lets CSS that resizes a glyph round to the same step.
  const grid = Math.max(width, height);
  const drawn = Math.max(grid, Math.floor(size / grid) * grid);
  return (
    <svg
      data-testid={testId}
      data-glyph={visualId}
      data-grid={grid}
      viewBox={`0 0 ${width} ${height}`}
      width={drawn}
      height={drawn}
      aria-hidden="true"
      focusable="false"
      shapeRendering="crispEdges"
      className={className}
    >
      {glyph === undefined ? (
        <>
          <rect x="1" y="1" width="7" height="7" fill={COLORS.glyphFallbackArt} />
          <rect x="4" y="2" width="1" height="4" fill={COLORS.glyphFallbackArtMark} />
          <rect x="4" y="7" width="1" height="1" fill={COLORS.glyphFallbackArtMark} />
        </>
      ) : glyphPaths(glyph).map((path) => (
        <path key={path.fill} d={path.d} fill={path.fill} />
      ))}
    </svg>
  );
});
